// @flow
import React, { Component } from 'react'
import _ from 'lodash'
import { Table, HeaderCell, HeaderRow, Cell, BodyRow, Link } from './MainTable'
import styled, { type StyledComponent } from 'styled-components'
import TargetingSourcesCoverage from './controls/TargetingSourcesCoverage'
import type { Segment, Contact, Lookalike, ProviderTargetingSource, ProviderIcons } from './types'

const EmptySourceWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 15px;
  padding-bottom: 15px;
`

type Props = {|
  segments: Array<Segment>,
  contacts: Array<Contact>,
  lookalikes: Array<Lookalike>,
  providerTargetingSources: Array<ProviderTargetingSource>,
  icons: ProviderIcons
|}

const newAudienceUrl = '/audiences/new'

const t = {
  contacts: 'Сегменты из загруженных контактов',
  domain: 'Домен',
  empty_source_message: 'Ещё не создано. Сегменты создаются при ',
  kind: 'Тип',
  list_size: 'Размер',
  lookalikes: 'Похожие сегменты',
  name: 'Название',
  new_audience_link_text: 'создании рекламы',
  origin_name: 'Источник',
  coverage: 'Размер в сетях',
  segments: 'Сегменты ретаргетинга с сайта',
  substring: 'Подстрока',
  provider_targeting_sources: 'Импортированные сегменты'
}

export default class TargetingSourcesTable extends Component<Props> {
  render() {
    const { segments, contacts, lookalikes, providerTargetingSources } = this.props
    return (
      <div>
        <div>
          <h3>{t.segments}</h3>
          {_.isEmpty(segments) ? this.renderEmptySourceMessage() : this.renderSegmentsTable()}
        </div>
        <div>
          <h3>{t.contacts}</h3>
          {_.isEmpty(contacts) ? this.renderEmptySourceMessage() : this.renderContactsTable()}
        </div>
        <div>
          <h3>{t.lookalikes}</h3>
          {_.isEmpty(lookalikes) ? this.renderEmptySourceMessage() : this.renderLookalikesTable()}
        </div>
        <div>
          <h3>{t.provider_targeting_sources}</h3>
          {_.isEmpty(providerTargetingSources)
            ? this.renderEmptySourceMessage()
            : this.renderProviderTargetingSourcesTable()}
        </div>
      </div>
    )
  }

  renderSegmentsTable() {
    const { segments } = this.props
    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="20%" alignLeft>
              {t.name}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {t.substring}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {t.domain}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft>
              {t.coverage}
            </HeaderCell>
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(segments, (segment) => {
            return (
              <BodyRow key={`segment_${segment.id}`}>
                <Cell>
                  <Link href={`/segments/${segment.id}/edit`} target="_blank">
                    {segment.name}
                  </Link>
                </Cell>
                <Cell>{segment.substrings}</Cell>
                <Cell>{segment.domain}</Cell>
                <Cell>
                  <TargetingSourcesCoverage icons={this.props.icons} coverage={segment.coverage} />
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderContactsTable() {
    const { contacts } = this.props
    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="20%" alignLeft>
              {t.name}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {t.kind}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {t.list_size}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft>
              {t.coverage}
            </HeaderCell>
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(contacts, (contact) => {
            return (
              <BodyRow key={`contact_${contact.id}`}>
                <Cell>{contact.name}</Cell>
                <Cell>{renderContactKindIcon(contact.kind)}</Cell>
                <Cell>{contact.listSize}</Cell>
                <Cell>
                  <TargetingSourcesCoverage icons={this.props.icons} coverage={contact.coverage} />
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderLookalikesTable() {
    const { lookalikes } = this.props
    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="40%" alignLeft>
              {t.name}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {t.origin_name}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft>
              {t.coverage}
            </HeaderCell>
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(lookalikes, (lookalike) => {
            return (
              <BodyRow key={`lookalike_${lookalike.name}_${lookalike.id}`}>
                <Cell>{lookalike.name}</Cell>
                <Cell>{lookalike.originName}</Cell>
                <Cell>
                  <TargetingSourcesCoverage
                    icons={this.props.icons}
                    coverage={lookalike.coverage}
                  />
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderProviderTargetingSourcesTable() {
    const { providerTargetingSources } = this.props
    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="40%" alignLeft>
              {t.name}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft>
              {t.coverage}
            </HeaderCell>
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(providerTargetingSources, (source) => {
            return (
              <BodyRow key={`source_${source.name}_${source.id}`}>
                <Cell>{source.name}</Cell>
                <Cell>
                  <TargetingSourcesCoverage icons={this.props.icons} coverage={source.coverage} />
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderEmptySourceMessage() {
    return (
      <EmptySourceWrapper>
        {t.empty_source_message}
        <Link href={newAudienceUrl}>{t.new_audience_link_text}</Link>
      </EmptySourceWrapper>
    )
  }
}

export function renderContactKindIcon(kind: 'phone' | 'email' | 'vk_id') {
  switch (kind) {
    case 'phone':
      return <i className="glyphicon glyphicon-phone" />
    case 'email':
      return <i className="glyphicon glyphicon-envelope" />
    case 'vk_id':
      return <i className="glyphicon glyphicon-user" />
  }
}
